import * as React from 'react';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import { useStore } from '@base-ui/utils/store';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useSchedulerStoreContext } from '@mui/x-scheduler-internals/use-scheduler-store-context';
import { schedulerEventSelectors, schedulerOtherSelectors, schedulerRecurringEventSelectors, schedulerResourceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import EventDialogHeader from "./EventDialogHeader.mjs";
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { getRecurrenceLabel, hasProp } from "./utils.mjs";
import { useFormatTime } from "../../hooks/useFormatTime.mjs";
import { getPaletteVariants } from "../../utils/tokens.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ReadonlyContentDragContainer = styled('section', {
  name: 'MuiEventDialog',
  slot: 'ReadonlyContentDragContainer'
})({
  cursor: 'move'
});
const ReadonlyContentRoot = styled('div', {
  name: 'MuiEventDialog',
  slot: 'ReadonlyContent'
})(({
  theme
}) => ({
  display: 'flex',
  padding: theme.spacing(0, 3),
  flexDirection: 'column',
  gap: theme.spacing(2)
}));
const RecurrenceLabelContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'RecurrenceLabelContainer'
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(1)
}));
const EventDialogActions = styled('div', {
  name: 'MuiEventDialog',
  slot: 'Actions'
})(({
  theme
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(3)
}));
const EventDialogDateTimeContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'DateTimeContainer'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const EventDialogTitle = styled(Typography, {
  name: 'MuiEventDialog',
  slot: 'Title'
})({
  margin: 0,
  color: 'var(--event-on-surface-subtle-primary)'
});
const EventDialogResourceContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'ResourceContainer'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const EventDialogResourceLegendContainer = styled('div', {
  name: 'MuiEventDialog',
  slot: 'ResourceLegendContainer'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const ResourceLegendColorDot = styled('span', {
  name: 'MuiEventDialog',
  slot: 'ResourceLegendColor'
})(({
  theme
}) => ({
  width: 14,
  height: 14,
  margin: theme.spacing(0, 0.2),
  borderRadius: 2,
  flexShrink: 0,
  backgroundColor: 'var(--event-main)',
  variants: getPaletteVariants(theme)
}));
const EventDialogResourceTitle = styled(Typography, {
  name: 'MuiEventDialog',
  slot: 'ResourceTitle'
})(({
  theme
}) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  color: 'var(--event-on-surface-subtle-secondary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));
export default function ReadonlyContent(props) {
  const {
    occurrence,
    onClose,
    dragHandlerRef
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const {
    schedulerId,
    classes,
    localeText
  } = useEventDialogStyledContext();
  const store = useSchedulerStoreContext();

  // Selector hooks
  const color = useStore(store, schedulerEventSelectors.color, occurrence.id);
  const resource = useStore(store, schedulerResourceSelectors.processedResource, occurrence.resource);
  const defaultRecurrenceKey = useStore(store, schedulerRecurringEventSelectors.defaultPresetKey, occurrence.displayTimezone.rrule, occurrence.displayTimezone.start);
  const showRecurrence = useStore(store, schedulerOtherSelectors.areRecurringEventsAvailable);

  // Feature hook
  const formatTime = useFormatTime();
  const recurrenceLabel = getRecurrenceLabel(adapter, occurrence.displayTimezone.start, defaultRecurrenceKey, localeText);
  return /*#__PURE__*/_jsxs(ReadonlyContentDragContainer, {
    ref: dragHandlerRef,
    children: [/*#__PURE__*/_jsx(EventDialogHeader, {
      onClose: onClose,
      children: /*#__PURE__*/_jsx(EventDialogTitle, {
        variant: "h6",
        id: `${schedulerId}-event-dialog-title`,
        className: classes.eventDialogTitle,
        children: occurrence.title
      })
    }), /*#__PURE__*/_jsxs(ReadonlyContentRoot, {
      className: classes.eventDialogReadonlyContent,
      children: [/*#__PURE__*/_jsxs(EventDialogResourceContainer, {
        className: classes.eventDialogResourceContainer,
        children: [/*#__PURE__*/_jsxs(EventDialogResourceLegendContainer, {
          className: classes.eventDialogResourceLegendContainer,
          children: [resource?.eventColor && resource.eventColor !== color && /*#__PURE__*/_jsx(ResourceLegendColorDot, {
            className: classes.eventDialogResourceLegendColor,
            "data-palette": resource.eventColor
          }), /*#__PURE__*/_jsx(ResourceLegendColorDot, {
            className: classes.eventDialogResourceLegendColor,
            "data-palette": color
          })]
        }), /*#__PURE__*/_jsx(EventDialogResourceTitle, {
          className: classes.eventDialogResourceTitle,
          children: resource?.title || localeText.noResourceAriaLabel
        })]
      }), /*#__PURE__*/_jsxs(EventDialogDateTimeContainer, {
        className: classes.eventDialogDateTimeContainer,
        children: [/*#__PURE__*/_jsx(CalendarMonthRounded, {
          className: classes.eventDialogDateTimeIcon,
          fontSize: "small"
        }), /*#__PURE__*/_jsxs(Typography, {
          className: classes.eventDialogDateTimeLabel,
          variant: "body2",
          component: "p",
          noWrap: true,
          children: [/*#__PURE__*/_jsx("time", {
            dateTime: adapter.format(occurrence.displayTimezone.start.value, 'localizedNumericDate'),
            children: /*#__PURE__*/_jsxs("span", {
              children: [adapter.format(occurrence.displayTimezone.start.value, 'localizedDateWithFullMonthAndWeekDay'), ",", ' ']
            })
          }), occurrence.allDay ? /*#__PURE__*/_jsxs("span", {
            children: [" ", localeText.allDayLabel]
          }) : /*#__PURE__*/_jsxs("time", {
            children: [/*#__PURE__*/_jsx("span", {
              children: formatTime(occurrence.displayTimezone.start.value)
            }), /*#__PURE__*/_jsxs("span", {
              children: [" - ", formatTime(occurrence.displayTimezone.end.value)]
            })]
          })]
        })]
      }), showRecurrence && defaultRecurrenceKey != null && /*#__PURE__*/_jsxs(RecurrenceLabelContainer, {
        className: classes.eventDialogRecurrenceLabelContainer,
        children: [/*#__PURE__*/_jsx(RepeatRoundedIcon, {
          className: classes.eventDialogRecurrenceIcon,
          fontSize: "small"
        }), /*#__PURE__*/_jsx(Typography, {
          className: classes.eventDialogRecurrenceLabel,
          variant: "body2",
          component: "em",
          sx: {
            color: 'text.secondary'
          },
          children: recurrenceLabel
        })]
      }), hasProp(occurrence, 'description') && !!occurrence.description ? /*#__PURE__*/_jsx(Typography, {
        className: classes.eventDialogDescriptionLabel,
        variant: "body2",
        children: occurrence.description
      }) : null]
    }), /*#__PURE__*/_jsx(EventDialogActions, {
      className: classes.eventDialogActions,
      children: /*#__PURE__*/_jsx(Button, {
        className: classes.eventDialogCloseAction,
        variant: "contained",
        type: "button",
        onClick: onClose,
        children: localeText.closeButtonLabel
      })
    })]
  });
}